import { memo, useState } from 'react'
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  TextField,
  Chip,
  IconButton,
  Typography,
  Stack,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import AddIcon from '@mui/icons-material/Add'
import type { OrcaQueryFieldAnnotation } from './types.js'

type Props = {
  name: string
  annotation: OrcaQueryFieldAnnotation
  /** Receives the field name so a single stable callback can serve every row. */
  onChange: (name: string, updated: OrcaQueryFieldAnnotation) => void
}

/**
 * Editor for one resolved query field. The `type` is generated from the merged
 * Orca schema and shown read-only; the user fills in meaning, unit, examples and
 * an optional cross-reference. Memoized so editing one field does not re-render
 * every other field row.
 */
export const OrcaQueryFieldEditor = memo(
  ({ name, annotation, onChange }: Props) => {
    const [exampleInput, setExampleInput] = useState('')

    const addExample = () => {
      const val = exampleInput.trim()
      if (!val) return
      let parsed: unknown
      try {
        parsed = JSON.parse(val)
      } catch {
        parsed = val
      }
      onChange(name, {
        ...annotation,
        examples: [...(annotation.examples ?? []), parsed],
      })
      setExampleInput('')
    }

    const removeExample = (i: number) =>
      onChange(name, {
        ...annotation,
        examples: annotation.examples?.filter((_, idx) => idx !== i),
      })

    const documented = annotation.description.trim().length > 0

    return (
      <Accordion disableGutters variant="outlined">
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
            mr={1}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <Typography
                variant="body2"
                fontWeight={600}
                fontFamily="monospace"
              >
                {name}
              </Typography>
              <Chip
                label={annotation.type}
                size="small"
                variant="outlined"
                sx={{ fontFamily: 'monospace', fontSize: 11 }}
              />
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              {annotation.unit && (
                <Chip
                  label={annotation.unit}
                  size="small"
                  color="info"
                  variant="outlined"
                />
              )}
              {!documented && (
                <Chip
                  label="undokumentiert"
                  size="small"
                  color="warning"
                  variant="outlined"
                />
              )}
            </Box>
          </Box>
        </AccordionSummary>

        <AccordionDetails>
          <Stack spacing={2}>
            <TextField
              label="Bedeutung"
              size="small"
              fullWidth
              value={annotation.description}
              onChange={(e) =>
                onChange(name, { ...annotation, description: e.target.value })
              }
              placeholder="Was misst/bedeutet dieses Feld?"
            />

            <TextField
              label="Einheit (z.B. °C, kWh)"
              size="small"
              fullWidth
              value={annotation.unit ?? ''}
              onChange={(e) =>
                onChange(name, {
                  ...annotation,
                  unit: e.target.value || undefined,
                })
              }
              placeholder="Einheit"
            />

            <TextField
              label="Referenz auf (z.B. machines.id)"
              size="small"
              fullWidth
              value={annotation.relatesTo ?? ''}
              onChange={(e) =>
                onChange(name, {
                  ...annotation,
                  relatesTo: e.target.value || undefined,
                })
              }
              placeholder="collection.field"
            />

            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={600}
                display="block"
                mb={1}
              >
                Beispielwerte
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={0.5} mb={1}>
                {annotation.examples?.map((ex, i) => (
                  <Chip
                    key={i}
                    label={JSON.stringify(ex)}
                    size="small"
                    variant="outlined"
                    onDelete={() => removeExample(i)}
                    sx={{ fontFamily: 'monospace', fontSize: 11 }}
                  />
                ))}
              </Box>
              <Box display="flex" gap={1}>
                <TextField
                  size="small"
                  value={exampleInput}
                  onChange={(e) => setExampleInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addExample()}
                  placeholder="Wert (JSON oder Text)…"
                />
                <IconButton size="small" onClick={addExample} color="primary">
                  <AddIcon />
                </IconButton>
              </Box>
            </Box>
          </Stack>
        </AccordionDetails>
      </Accordion>
    )
  },
)
OrcaQueryFieldEditor.displayName = 'OrcaQueryFieldEditor'
